import ResourceManager from "../../components/ResourceManager"
import { StringMap } from "i18next"
import { useI18n } from "../../hooks"
import { useOptions } from "../../options/hooks/use-options"
import { useProjectMutations } from "../hooks/use-project-mutations"
import { ProjectViewModel } from "../api/project"

const ProjectWorkspacesWidget = ({ project }: { project: ProjectViewModel }) => {
  const translations = useTranslations(defaultTranslations)
  const { workspaceOptions } = useOptions(project.maintainerId)
  const { addWorkspaces, removeWorkspaces } = useProjectMutations()

  const handleAddWorkspaces = (ids: string[]) => {
    const workspaces = workspaceOptions
      .filter((workspace) => ids.includes(workspace.id))
      .map((workspace) => ({ name: workspace.name, id: workspace.id }))
    addWorkspaces(project.id, workspaces)
  }

  const handleRemoveWorkspace = (id: string) => {
    const workspaces = project.workspaces
      .filter((workspace) => workspace.id === id)
      .map((workspace) => ({ name: workspace.name, id: workspace.id }))
    removeWorkspaces(project.id, workspaces)
  }

  const sortedWorkspaceOptions = workspaceOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedProjectWorkspaces = project.workspaces
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedWorkspaceOptions}
      resources={sortedProjectWorkspaces}
      canAddResource={project.canUpdateWorkspaces}
      canRemoveResource={project.canUpdateWorkspaces}
      onAddResources={handleAddWorkspaces}
      onRemoveResource={handleRemoveWorkspace}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t } = useI18n("project")
  const translations = (t?.workspacesWidget || {}) as StringMap

  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Workspaces",
  emptyOptionsMessage: "No workspaces in this organisation",
  emptyResourcesMessage: "Project is not connected to any workspace",
}

type Translations = typeof defaultTranslations

export default ProjectWorkspacesWidget
